<template>
    <el-drawer :visible.sync="isVisible" direction="rtl" size="580px" :with-header="false"
        custom-class="create-custom-venue-drawer" :before-close="handleClose">
        <div class="create-custom-venue" v-loading="loading">
            <div class="create-custom-venue__header">
                <h4 class="create-custom-venue__title mb-0">{{ isEditMode ? 'Edit Venue' : 'Create Venue' }}</h4>
                <button type="button" class="create-custom-venue__close-btn" @click="handleClose" aria-label="Close">
                    <i class="ni ni-fat-remove"></i>
                </button>
            </div>

            <div class="create-custom-venue__body">
                <p class="create-custom-venue__section-title mb-0">Venue Details</p>

                <div class="create-custom-venue__grid">
                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">
                            Name
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-input v-model="formData.name" placeholder="Enter venue name"
                            class="create-custom-venue__input" @input="onFieldChanged('name')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">
                            Address
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <GmapAutocomplete ref="addressAutocomplete"
                            :key="`address-autocomplete-${autocompleteCountryCode}`"
                            class="form-control create-custom-venue__address-input" placeholder="Search address"
                            :options="autocompleteOptions" @place_changed="onAddressSelected">
                        </GmapAutocomplete>
                        <span v-if="errors.address" class="error-message">{{ errors.address }}</span>
                    </div>

                    <div class="create-custom-venue__field">
                        <label class="create-custom-venue__label">
                            Country
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-select v-model="formData.country" placeholder="Select country"
                            class="create-custom-venue__select" @change="handleCountryChange">
                            <el-option v-for="countryOption in countryOptions" :key="countryOption.value"
                                :label="countryOption.label" :value="countryOption.value">
                            </el-option>
                        </el-select>
                        <span v-if="errors.country" class="error-message">{{ errors.country }}</span>
                    </div>

                    <div class="create-custom-venue__field">
                        <label class="create-custom-venue__label">
                            Suburb
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-input v-model="formData.suburb" placeholder="Enter venue suburb"
                            class="create-custom-venue__input" @input="onFieldChanged('suburb')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.suburb" class="error-message">{{ errors.suburb }}</span>
                    </div>

                    <div class="create-custom-venue__field">
                        <label class="create-custom-venue__label">
                            State
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-input v-model="formData.state" placeholder="Enter venue state"
                            class="create-custom-venue__input" @input="onFieldChanged('state')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.state" class="error-message">{{ errors.state }}</span>
                    </div>

                    <div class="create-custom-venue__field">
                        <label class="create-custom-venue__label">
                            Postcode
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-input v-model="formData.postcode" placeholder="Enter venue postcode"
                            class="create-custom-venue__input" @input="onFieldChanged('postcode')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.postcode" class="error-message">{{ errors.postcode }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label"
                            :class="{ 'create-custom-venue__label--highlight': highlightParentCity }">
                            Parent City
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-select v-model="formData.parent_city" filterable placeholder="Select parent city"
                            class="create-custom-venue__select"
                            :class="{ 'create-custom-venue__select--highlight': highlightParentCity }"
                            @change="handleParentCityChange">
                            <el-option v-for="location in parentCityOptions" :key="location.id || location.name"
                                :label="location.name" :value="location.name">
                            </el-option>
                        </el-select>
                        <span v-if="errors.parent_city" class="error-message">{{ errors.parent_city }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">
                            Inspection Contact Name
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-input v-model="formData.inspection_contact_name" placeholder="Enter contact name"
                            class="create-custom-venue__input" @input="onFieldChanged('inspection_contact_name')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.inspection_contact_name" class="error-message">{{
                            errors.inspection_contact_name }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full inspection-contact-data">
                        <div class="create-custom-venue__field">
                            <label class="create-custom-venue__label">
                                Inspection Email
                                <span class="create-custom-venue__required">*</span>
                            </label>
                            <el-input v-model="formData.inspection_contact_email" placeholder="Enter contact email"
                                class="create-custom-venue__input" @input="onFieldChanged('inspection_contact_email')"
                                @keyup.enter.native="submit"></el-input>
                            <span v-if="errors.inspection_contact_email" class="error-message">{{
                                errors.inspection_contact_email }}</span>
                        </div>


                        <div class="create-custom-venue__field">
                            <label class="create-custom-venue__label">
                                Inspection Contact Phone
                            </label>
                            <el-input v-model="formData.inspection_contact_phone" placeholder="Enter contact phone"
                                class="create-custom-venue__input" @input="onFieldChanged('inspection_contact_phone')"
                                @keyup.enter.native="submit"></el-input>
                            <span v-if="errors.inspection_contact_phone" class="error-message">{{
                                errors.inspection_contact_phone }}</span>
                        </div>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">
                            Time Zone
                            <span class="create-custom-venue__required">*</span>
                        </label>
                        <el-select v-model="formData.timezone" filterable placeholder="Select a time zone"
                            class="create-custom-venue__select" @change="onFieldChanged('timezone')">
                            <el-option v-for="timezone in allTimezones" :key="timezone" :label="timezone"
                                :value="timezone">
                            </el-option>
                        </el-select>
                        <span v-if="errors.timezone" class="error-message">{{ errors.timezone }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">Key Selling Points</label>
                        <el-input v-model="formData.key_selling_points[0]" placeholder="Enter USP 1"
                            class="create-custom-venue__input" @keyup.enter.native="submit"></el-input>
                        <el-input v-model="formData.key_selling_points[1]" placeholder="Enter USP 2"
                            class="create-custom-venue__input mt-3" @keyup.enter.native="submit"></el-input>
                        <el-input v-model="formData.key_selling_points[2]" placeholder="Enter USP 3"
                            class="create-custom-venue__input mt-3" @keyup.enter.native="submit"></el-input>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">Description</label>
                        <el-input v-model="formData.description" type="textarea" :rows="3"
                            placeholder="Enter venue description" class="create-custom-venue__textarea"></el-input>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">Venue Link</label>
                        <el-input v-model="formData.link" placeholder="https://example.com"
                            class="create-custom-venue__input" @input="onFieldChanged('link')"
                            @keyup.enter.native="submit"></el-input>
                        <span v-if="errors.link" class="error-message">{{ errors.link }}</span>
                    </div>

                    <div class="create-custom-venue__field create-custom-venue__field--full">
                        <label class="create-custom-venue__label">Hero Image
                            <span v-if="! isEditMode" class="create-custom-venue__required">*</span>
                        </label>
                        <input ref="heroImageInput" type="file" accept="image/*" class="d-none"
                            @change="onHeroImageSelected" />
                        <button type="button" class="create-custom-venue__upload-btn" @click="openFilePicker">
                            <i class="fas fa-upload create-custom-venue__upload-icon"></i>
                            Click to upload or change image
                        </button>
                        <img v-if="heroImagePreview" :src="heroImagePreview" alt="Hero image preview"
                            class="create-custom-venue__hero-preview" />
                        <span v-if="errors.hero_image || errors.thumbnail" class="error-message">{{
                            errors.hero_image || errors.thumbnail }}</span>
                    </div>
                </div>
            </div>

            <div class="create-custom-venue__footer">
                <base-button type="primary" native-type="button" class="create-custom-venue__save-btn"
                    @click="submit"
                    :disabled="! isValid"
                >
                    {{ isEditMode ? 'Update Venue' : 'Save Venue' }}
                </base-button>
            </div>
        </div>
    </el-drawer>
</template>

<script>
import {
    Drawer,
    Input,
    Select,
    Option,
    Button,
} from "element-ui";
import { mapState } from "vuex";
import { CustomVenueService, LeadEventService } from "@/services";
import timezoneMixin from "@/helpers/timezoneMixin";

export default {
    name: "create-custom-venue-drawer",
    components: {
        [Drawer.name]: Drawer,
        [Input.name]: Input,
        [Select.name]: Select,
        [Option.name]: Option,
        [Button.name]: Button,
    },
    mixins: [timezoneMixin],
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        venue: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            parentCityOptions: [],
            venues: [],
            highlightParentCity: false,
            heroImagePreview: "",
            errors: {},
            countryOptions: [
                { label: "Australia", value: "AU" },
                { label: "United Kingdom", value: "UK" },
            ],
            cityTimezoneMap: this.getCityTimezoneMap(),
            formData: this.getDefaultFormData()

        };
    },
    computed: {
        ...mapState("country/", {
            country: (state) => state.country || "AU",
        }),
        autocompleteCountryCode() {
            const selectedCountry = this.formData.country || this.country || "";
            const autocompleteCountryMap = {
                AU: "au",
                UK: "gb",
                GB: "gb",
            };

            return autocompleteCountryMap[selectedCountry] || selectedCountry.toLowerCase();
        },
        autocompleteOptions() {
            return {
                componentRestrictions: {
                    country: this.autocompleteCountryCode ? [this.autocompleteCountryCode] : [],
                },
            };
        },
        isVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit("update:visible", value);
            },
        },
        isValid() {
            return Object.keys(this.errors).length === 0;
        },
        isEditMode() {
            return !!this.venue;
        }
    },
    watch: {
        isVisible(newValue) {
            if (newValue) {
                this.resetState();
            }
        },
    },
    async created() {
        this.loading = true;
        await Promise.all([
            this.$store.dispatch("venues/getAll", { country: this.country }),
            this.fetchParentCities(),
        ]);
        this.venues = this.$store.state.venues.items || [];
        this.loading = false;
    },
    methods: {
        getCityTimezoneMap() {
            return {
                sydney: "Australia/Sydney",
                melbourne: "Australia/Melbourne",
                brisbane: "Australia/Brisbane",
                perth: "Australia/Perth",
                adelaide: "Australia/Adelaide",
                canberra: "Australia/Sydney",
                hobart: "Australia/Melbourne",
                darwin: "Australia/Adelaide",
                goldcoast: "Australia/Brisbane",
                "gold coast": "Australia/Brisbane",
                sunshinecoast: "Australia/Brisbane",
                "sunshine coast": "Australia/Brisbane",
                newcastle: "Australia/Sydney",
                wollongong: "Australia/Sydney",
                geelong: "Australia/Melbourne",
                london: "Europe/London",
                manchester: "Europe/London",
                birmingham: "Europe/London",
                leeds: "Europe/London",
                liverpool: "Europe/London",
                bristol: "Europe/London",
                edinburgh: "Europe/London",
                glasgow: "Europe/London",
                cardiff: "Europe/London",
                belfast: "Europe/London",
                dublin: "Europe/Dublin",
            };
        },

        getDefaultFormData() {
            return {
                name: "",
                suburb: "",
                state: "",
                postcode: "",
                city: "",
                parent_city: "",
                timezone: "",
                inspection_contact_name: "",
                inspection_contact_email: "",
                inspection_contact_phone: "",
                description: "",
                link: "",
                key_selling_points: ["", "", ""],
                latitude: null,
                longitude: null,
                place_id: "",
                country: this.country,
                is_active: true,
                thumbnail: "",
                address_text: "",
                route_name: "",
                route_name_short: "",
                street_number: "",
                state_short: "",
                country_long: "",
                country_short: "",
            };
        },

        async fetchParentCities(countryCode = this.formData.country || this.country) {
            try {
                const response = await LeadEventService.getEventLocations(countryCode);
                this.parentCityOptions = response?.data?.eventLocations || [];
            } catch (error) {
                this.parentCityOptions = [];
            }
        },

        async handleCountryChange(countryCode) {
            this.formData.country = countryCode;
            this.formData.suburb = "";
            this.formData.city = "";
            this.formData.state = "";
            this.formData.postcode = "";
            this.formData.latitude = null;
            this.formData.longitude = null;
            this.formData.place_id = "";
            this.formData.country_long = "";
            this.formData.country_short = "";
            this.formData.route_name = "";
            this.formData.route_name_short = "";
            this.formData.street_number = "";
            this.formData.state_short = "";
            this.formData.address_text = "";
            this.formData.parent_city = "";
            this.formData.timezone = "";
            await this.fetchParentCities(countryCode);
            this.highlightParentCity = false;
            this.clearFieldError("address");
            this.onFieldChanged("country");

            if (this.$refs.addressAutocomplete && this.$refs.addressAutocomplete.$el) {
                this.$refs.addressAutocomplete.$el.value = "";
            }
        },

        parseAddressComponent(addressComponents = [], type) {
            const component = addressComponents.find((item) => item.types.includes(type));
            if (!component) {
                return { long: "", short: "" };
            }

            return {
                long: component.long_name || "",
                short: component.short_name || "",
            };
        },

        onFieldChanged(field) {
            this.clearFieldError(field);
        },

        clearFieldError(field) {
            // must use this.$delete to ensure reactivity when removing object keys in vue2
            if (Object.prototype.hasOwnProperty.call(this.errors, field)) {
                this.$delete(this.errors, field);
            }
        },

        normalizeCityKey(city) {
            if (!city) {
                return "";
            }

            return city
                .toString()
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, "")
                .replace(/\s+/g, " ");
        },

        getTimezoneByParentCity(city, mapOverride = null) {
            const normalizedCity = this.normalizeCityKey(city);
            if (!normalizedCity) {
                return "";
            }

            const cityTimezoneMap = mapOverride || this.cityTimezoneMap || this.getCityTimezoneMap();
            return cityTimezoneMap[normalizedCity] || "";
        },

        handleParentCityChange(parentCity) {
            this.highlightParentCity = false;
            this.onFieldChanged("parent_city");

            const matchedTimezone = this.getTimezoneByParentCity(parentCity);
            if (matchedTimezone) {
                this.formData.timezone = matchedTimezone;
                this.onFieldChanged("timezone");
            }
        },

        onAddressSelected(placePayload) {
            const place = this.$refs.addressAutocomplete?.getPlace?.() || placePayload;

            if (!place || !place.geometry || !place.geometry.location) {
                return;
            }

            const suburb = this.parseAddressComponent(place.address_components, "locality");
            const postalTown = this.parseAddressComponent(place.address_components, "postal_town");
            const adminAreaLevelTwo = this.parseAddressComponent(place.address_components, "administrative_area_level_2");
            const state = this.parseAddressComponent(place.address_components, "administrative_area_level_1");
            const postcode = this.parseAddressComponent(place.address_components, "postal_code");
            const country = this.parseAddressComponent(place.address_components, "country");
            const route = this.parseAddressComponent(place.address_components, "route");
            const streetNumber = this.parseAddressComponent(place.address_components, "street_number");

            const resolvedCity = suburb.long || postalTown.long || adminAreaLevelTwo.long;
            const selectedCountry = this.formData.country || this.country;
            const selectedCountryMap = {
                AU: ["AU"],
                UK: ["GB", "UK"],
                GB: ["GB", "UK"],
            };
            const allowedCountryShortCodes = selectedCountryMap[selectedCountry] || [selectedCountry];

            if (!allowedCountryShortCodes.includes((country.short || "").toUpperCase())) {
                this.errors = {
                    ...this.errors,
                    address: "Please select an address in the chosen country.",
                };
                return;
            }

            this.clearFieldError("address");

            this.formData.suburb = suburb.long || postalTown.long;
            this.formData.city = resolvedCity;
            this.formData.state = state.long;
            this.formData.postcode = postcode.long;
            this.formData.latitude = place.geometry.location.lat();
            this.formData.longitude = place.geometry.location.lng();
            this.formData.place_id = place.place_id || "";
            this.formData.country_long = country.long;
            this.formData.country_short = country.short;
            this.formData.route_name = route.long;
            this.formData.route_name_short = route.short;
            this.formData.street_number = streetNumber.long;
            this.formData.state_short = state.short;
            this.formData.address_text = place.formatted_address || "";
            this.formData.parent_city = "";
            this.highlightParentCity = true;
        },

        openFilePicker() {
            this.$refs.heroImageInput.click();
        },

        onHeroImageSelected(event) {
            const file = event.target.files[0];
            if (! file) {
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                this.formData.thumbnail = reader.result;
                this.heroImagePreview = reader.result;
                this.clearFieldError("hero_image");
                this.clearFieldError("thumbnail");
            };
            reader.readAsDataURL(file);
        },

        extractFirstErrorMessage(errorValue) {
            if (Array.isArray(errorValue)) {
                return errorValue[0] || "";
            }

            return errorValue || "";
        },

        setServerValidationErrors(serverErrors) {
            if (! serverErrors || typeof serverErrors !== "object") {
                return false;
            }

            const validationErrors = {};
            Object.keys(serverErrors).forEach((field) => {
                const firstErrorMessage = this.extractFirstErrorMessage(serverErrors[field]);
                if (firstErrorMessage) {
                    validationErrors[field] = firstErrorMessage;
                }
            });

            if (Object.keys(validationErrors).length === 0) {
                return false;
            }

            this.errors = {
                ...this.errors,
                ...validationErrors,
            };

            return true;
        },

        venueNameExists() {
            const trimmedName = this.formData.name.trim().toLowerCase();
            if (! trimmedName) {
                return false;
            }

            return this.venues.some((venue) => {
                return venue?.name?.trim()?.toLowerCase() === trimmedName;
            });
        },

        validateEmail(value) {
            if (!value) {
                return false;
            }

            const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return pattern.test(value);
        },

        validateUrl(value) {
            if (!value) {
                return true;
            }

            const pattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i;
            return pattern.test(value);
        },

        validateForm() {
            const errors = {};

            if (!this.formData.country) {
                errors.country = "Country is required.";
            }

            if (!this.formData.name.trim()) {
                errors.name = "Venue name is required.";
            }

            if (!this.isEditMode && this.venueNameExists()) {
                errors.name = "Venue name already exists as Venuecrew venue.";
            }
            if (!this.formData.address_text || this.formData.latitude === null || this.formData.longitude === null) {
                errors.address = "Address selection is required.";
            }

            if (!this.formData.suburb.trim()) {
                errors.suburb = "Suburb is required.";
            }

            if (!this.formData.state.trim()) {
                errors.state = "State is required.";
            }

            if (!this.formData.postcode.trim()) {
                errors.postcode = "Postcode is required.";
            }

            if (!this.formData.parent_city.trim()) {
                errors.parent_city = "Parent city is required.";
            }

            if (!this.formData.inspection_contact_name.trim()) {
                errors.inspection_contact_name = "Inspection contact name is required.";
            }

            if (!this.validateEmail(this.formData.inspection_contact_email)) {
                errors.inspection_contact_email = "A valid inspection contact email is required.";
            }

            if (!this.formData.timezone) {
                errors.timezone = "Time zone is required.";
            }

            if (!this.validateUrl(this.formData.link)) {
                errors.link = "Venue link must be a valid URL.";
            }

            this.errors = errors;
        },

        buildAddressPayload() {
            return {
                lat: this.formData.latitude,
                lng: this.formData.longitude,
                city: this.formData.city,
                name: this.formData.name,
                zoom: 14,
                state: this.formData.state,
                address: this.formData.address_text,
                country: this.formData.country_long,
                place_id: this.formData.place_id,
                post_code: this.formData.postcode,
                state_short: this.formData.state_short,
                street_name: this.formData.route_name,
                country_short: this.formData.country_short,
                street_number: this.formData.street_number,
                street_name_short: this.formData.route_name_short,
            };
        },

        buildPayload() {
            return {
                name: this.formData.name.trim(),
                country: this.formData.country,
                city: this.formData.city.trim(),
                parent_city: this.formData.parent_city.trim(),
                address: this.buildAddressPayload(),
                latitude: this.formData.latitude,
                longitude: this.formData.longitude,
                timezone: this.formData.timezone,
                hero_image: this.formData.thumbnail || null,
                description: this.formData.description.trim() || null,
                link: this.formData.link.trim() || null,
                key_selling_points: this.formData.key_selling_points.filter((item) => !!item.trim()),
                is_active: true,
                inspection_contact_name: this.formData.inspection_contact_name.trim(),
                inspection_contact_email: this.formData.inspection_contact_email.trim(),
                inspection_contact_phone: this.formData.inspection_contact_phone.trim() || null,
            };
        },

        async submit() {
            this.validateForm();
            if (Object.keys(this.errors).length > 0) {
                return;
            }

            this.loading = true;
            try {
                const payload = this.buildPayload();
                if (this.isEditMode) {
                    // Omit hero_image when unchanged so the existing image is preserved.
                    if (!this.formData.thumbnail) {
                        delete payload.hero_image;
                    }
                    const response = await CustomVenueService.updateCustomVenue(this.venue.id, payload);
                    this.$emit("venue-updated", response.custom_venue);
                    this.$toast.success("Custom venue updated successfully.");
                } else {
                    const response = await CustomVenueService.createCustomVenue(payload);
                    this.$emit("venue-created", response.custom_venue);
                    this.$toast.success("Custom venue created successfully.");
                }
                this.handleClose();
            } catch (error) {
                const responseData = error?.response?.data || {};
                const hasValidationErrors = this.setServerValidationErrors(responseData.errors);

                if (! hasValidationErrors) {
                    this.$store.dispatch("alert/error", error, { root: true });
                }
            } finally {
                this.loading = false;
            }
        },

        resetState() {
            this.formData = this.isEditMode ? this.getFormDataFromVenue(this.venue) : this.getDefaultFormData();
            this.heroImagePreview = this.isEditMode ? this.getVenueHeroImage(this.venue) : "";
            this.errors = {};
            this.highlightParentCity = false;
            if (this.$refs.heroImageInput) {
                this.$refs.heroImageInput.value = "";
            }
            if (this.$refs.addressAutocomplete && this.$refs.addressAutocomplete.$el) {
                this.$refs.addressAutocomplete.$el.value = this.formData.address_text;
            }
        },

        getVenueHeroImage(venue) {
            return venue.thumbnail || "";
        },

        // Maps an existing custom venue back into the create form's flat shape.
        getFormDataFromVenue(venue) {
            const address = venue.address && typeof venue.address === "object" ? venue.address : {};
            const sellingPoints = Array.isArray(venue.key_selling_points) ? venue.key_selling_points : [];

            return {
                name: venue.name || "",
                suburb: venue.city || address.city || "",
                state: address.state || "",
                postcode: address.post_code || "",
                city: venue.city || address.city || "",
                parent_city: venue.parent_city || "",
                timezone: venue.timezone || "",
                inspection_contact_name: venue.inspection_contact_name || "",
                inspection_contact_email: venue.inspection_contact_email || "",
                inspection_contact_phone: venue.inspection_contact_phone || "",
                description: venue.description || "",
                link: venue.link || "",
                key_selling_points: [sellingPoints[0] || "", sellingPoints[1] || "", sellingPoints[2] || ""],
                latitude: venue.lat ?? address.lat ?? null,
                longitude: venue.long ?? address.lng ?? null,
                place_id: address.place_id || "",
                country: venue.country || this.country,
                is_active: venue.is_active ?? true,
                thumbnail: "",
                address_text: address.address || (typeof venue.address === "string" ? venue.address : ""),
                route_name: address.street_name || "",
                route_name_short: address.street_name_short || "",
                street_number: address.street_number || "",
                state_short: address.state_short || "",
                country_long: address.country || "",
                country_short: address.country_short || "",
            };
        },

        handleClose() {
            this.$emit("update:visible", false);
            this.$emit("close");
            this.resetState();
        },
    },
};
</script>

<style scoped>
.create-custom-venue {
    background-color: #ffffff;
    box-shadow: -4px 0 24px 0 rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    height: 100%;
}

.create-custom-venue__header {
    align-items: center;
    border-bottom: 0.8px solid #e8ecf0;
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
}

.create-custom-venue__title {
    color: #1a2332;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
}

.create-custom-venue__close-btn {
    align-items: center;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: #525f7f;
    cursor: pointer;
    display: inline-flex;
    font-size: 18px;
    height: 30px;
    justify-content: center;
    width: 30px;
}

.create-custom-venue__body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;
}

.create-custom-venue__section-title {
    color: #8898aa;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.65px;
    line-height: 19.5px;
    text-transform: uppercase;
}

.create-custom-venue__grid {
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding-top: 20px;
}

.create-custom-venue__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.create-custom-venue__field.create-custom-venue__field--full.inspection-contact-data {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
}

.create-custom-venue__field--full {
    grid-column: 1 / -1;
}

.create-custom-venue__label {
    color: #525f7f;
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
    margin-bottom: 0;
}

.create-custom-venue__label--highlight {
    color: #0072ff;
}

.create-custom-venue__required {
    color: #ef4444;
}

.create-custom-venue__address-input {
    border: 0.8px solid #dde1e7;
    border-radius: 6px;
    color: #1a2332;
    font-size: 13px;
    height: 45px;
    line-height: 45px;
    padding: 0 12px;
}

.create-custom-venue__upload-btn {
    align-items: center;
    background-color: #ffffff;
    border: 1.6px dashed #dde1e7;
    border-radius: 4px;
    color: #6b7280;
    display: inline-flex;
    font-size: 13px;
    font-weight: 500;
    gap: 8px;
    justify-content: center;
    line-height: 19.5px;
    min-height: 45px;
    padding: 12px;
}

.create-custom-venue__upload-icon {
    font-size: 15px;
}

.create-custom-venue__hero-preview {
    border-radius: 4px;
    height: 100px;
    margin-top: 8px;
    object-fit: cover;
    width: 160px;
}

.create-custom-venue__footer {
    border-top: 0.8px solid #e8ecf0;
    padding: 16px 24px;
}

.create-custom-venue__save-btn {
    background-color: #0072ff !important;
    border-color: #0072ff !important;
    border-radius: 4px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    height: 41px;
    line-height: 21px !important;
    width: 100%;
}

.error-message {
    color: #ef4444;
    font-size: 13px;
}

::v-deep .create-custom-venue-drawer {
    max-width: 100%;
}

::v-deep .create-custom-venue-drawer .el-drawer__body {
    height: 100%;
    overflow: hidden;
    padding: 0;
}

::v-deep .create-custom-venue__input .el-input__inner,
::v-deep .create-custom-venue__select .el-input__inner,
::v-deep .create-custom-venue__textarea .el-textarea__inner {
    border: 0.8px solid #dde1e7;
    border-radius: 6px;
    color: #1a2332;
    font-size: 13px;
}

::v-deep .create-custom-venue__input .el-input__inner,
::v-deep .create-custom-venue__select .el-input__inner {
    height: 45px;
}

::v-deep .create-custom-venue__input .el-input__inner::placeholder,
::v-deep .create-custom-venue__select .el-input__inner::placeholder,
::v-deep .create-custom-venue__textarea .el-textarea__inner::placeholder,
.create-custom-venue__address-input::placeholder {
    color: rgba(26, 35, 50, 0.5);
    font-size: 13px;
}

::v-deep .create-custom-venue__select--highlight .el-input__inner {
    border-color: #0072ff;
    box-shadow: 0 0 0 1px rgba(0, 114, 255, 0.15);
}

@media (max-width: 768px) {
    .create-custom-venue__grid {
        grid-template-columns: repeat(1, minmax(0, 1fr));
    }
}
</style>

<style>
.pac-container {
    z-index: 3000 !important;
}
</style>
